import {
  Box,
  createStyles,
  darken,
  makeStyles,
  Theme,
} from '@material-ui/core';
import clsx from 'clsx';
import { FC } from 'react';
import {
  brighten,
  EntityIcon,
  getTaxonomyColor,
  WbTruncatedTypographyWrapper,
} from '@agilelab/plugin-wb-platform';

const getFgColorByUrn = (urn: string, theme: Theme) =>
  getTaxonomyColor(urn, theme);
const selectionBorderColorByUrn = (urn: string, theme: Theme) =>
  brighten(getTaxonomyColor(urn, theme));
const getBorderColorByUrn = (urn: string, theme: Theme) =>
  darken(getTaxonomyColor(urn, theme), 0.1);

const useEntityCardStyles = makeStyles<Theme, { taxonomyUrn: string }>(theme =>
  createStyles({
    root: ({ taxonomyUrn }) => ({
      // reset button styles
      appearance: 'none',
      width: '100%',
      cursor: 'pointer',
      padding: theme.spacing(2),
      display: 'flex',
      gap: theme.spacing(1),
      alignItems: 'center',
      border: `1px solid ${theme.palette.grey[300]}`,
      borderRadius: theme.spacing(0.5),
      position: 'relative',
      overflow: 'hidden',
      paddingInline: theme.spacing(2),
      backgroundColor: theme.palette.white,
      // left side decoration
      '&::before': {
        content: "''",
        width: 6,
        position: 'absolute',
        left: 0,
        height: '100%',
        backgroundColor: getFgColorByUrn(taxonomyUrn, theme),
      },
    }),
    truncated: {
      '-webkit-box-orient': 'vertical',
      '-webkit-line-clamp': 3,
      overflow: 'hidden',
      display: '-webkit-box',
      wordBreak: 'break-word',
      fontWeight: 'bold',
    },
    hex: ({ taxonomyUrn }) => ({
      color: getFgColorByUrn(taxonomyUrn, theme),
    }),
    labelContainer: {
      display: 'flex',
      flexDirection: 'column',
    },

    selected: ({ taxonomyUrn }) => ({
      border: `1px solid ${selectionBorderColorByUrn(taxonomyUrn, theme)}`,
    }),
    hover: ({ taxonomyUrn }) => ({
      '&:hover': {
        border: `1px solid ${getBorderColorByUrn(taxonomyUrn, theme)}`,
      },
    }),
    rightContainer: {
      marginLeft: 'auto',
    },
  }),
);

export type Entity = {
  id: string;
  externalId: string;
  displayName?: string;
  kind?: string;
  useCaseTemplateId?: string;
  taxonomyId?: string;
  components?: Entity[];
  _resolvedType?: string;
};

export type EntityCardProps = {
  entity: Entity;
  onClickHandler?: (externalId: string) => void;
  isActiveEntity?: boolean;
  rightContent?: React.ReactNode;
};

export const EntityCard: FC<EntityCardProps> = ({
  entity,
  isActiveEntity,
  onClickHandler,
  rightContent,
}) => {
  const classes = useEntityCardStyles({ taxonomyUrn: '' });

  return (
    <button
      className={clsx(
        classes.root,
        isActiveEntity && classes.selected,
        classes.hover,
      )}
      onClick={() => onClickHandler?.(entity.externalId)}
    >
      <EntityIcon
        useCaseTemplateId={entity.useCaseTemplateId ?? ' '}
        kind={entity.kind ?? ' '}
        title={entity.displayName ?? entity.externalId}
        taxonomyUrn={entity.taxonomyId}
        style={{ flexShrink: 0 }}
      />
      <Box className={clsx(classes.labelContainer, classes.truncated)}>
        <WbTruncatedTypographyWrapper
          value={entity.displayName ?? entity.externalId}
          maxLines={1}
          variant="body1"
          enableTooltip
          placement="top-start"
        />
      </Box>
      {rightContent && (
        <Box className={classes.rightContainer}>{rightContent}</Box>
      )}
    </button>
  );
};
